
/**
 * Recursively parse JSON strings into objects
 * Handles deeply nested JSON-encoded strings (up to 10 levels)
 *
 * @param data - The data to parse (string, object, array, or primitive)
 * @param depth - Current recursion depth (prevents infinite loops)
 * @returns Parsed data structure or null on error
 */
export function parseJSON(data: any, depth: number = 0): any {
    // Prevent infinite recursion
    if (depth > 10) {
        console.warn('⚠️ Max recursion depth reached in parseJSON');
        return data;
    }

    if (!data) return data;

    // Handle arrays - recursively parse each element
    if (Array.isArray(data)) {
        return data.map(item => parseJSON(item, depth + 1));
    }

    // Handle non-objects
    if (typeof data !== 'object') {
        // Try to parse strings that look like JSON
        if (typeof data === 'string') {
            const trimmed = data.trim();
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                    const parsed = JSON.parse(data);
                    // Recursively parse the result in case it contains more JSON strings
                    return parseJSON(parsed, depth + 1);
                } catch (e) {
                    // Not valid JSON, return as-is
                    return data;
                }
            }
        }
        return data;
    }

    // Handle objects - recursively parse all properties
    const parsed: any = {};
    for (const [key, value] of Object.entries(data)) {
        parsed[key] = parseJSON(value, depth + 1);
    }
    return parsed;
}

export function getInitials(name: string | null | undefined) {
    if (!name || name === 'N/A') return '--';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

export function getFlagEmoji(langCode: string | undefined) {
    const flags: Record<string, string> = {
        'en': '🇬🇧', 'es': '🇪🇸', 'fr': '🇫🇷', 'de': '🇩🇪',
        'it': '🇮🇹', 'pt': '🇵🇹', 'ru': '🇷🇺', 'ja': '🇯🇵',
        'ko': '🇰🇷', 'zh': '🇨🇳', 'ar': '🇸🇦', 'hi': '🇮🇳',
        'us': '🇺🇸', 'ca': '🇨🇦', 'gb': '🇬🇧'
    };
    return flags[langCode?.toLowerCase() || ''] || '🌐';
}

export function getLanguageFlag(language: string | undefined, countryCode: string | undefined) {
    if (language && language.toLowerCase() === 'english') {
        if (countryCode) {
            const code = countryCode.toLowerCase();
            if (code === 'us' || code === 'usa') return '🇺🇸';
            if (code === 'ca' || code === 'can') return '🇨🇦';
            if (code === 'gb' || code === 'uk') return '🇬🇧';
        }
        return '🇬🇧';
    }
    return getFlagEmoji(countryCode);
}

export function cleanURL(url: string | null | undefined) {
    if (!url || url === '#') return '#';
    try {
        const u = new URL(url);
        return u.hostname.replace('www.', '') + u.pathname;
    } catch {
        return url;
    }
}

export function getICPConfig(score: number) {
    if (score >= 85) {
        return {
            // Advanced Glassmorphism for Excellent Score
            wrapperClass: 'bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 backdrop-blur-2xl border border-emerald-500/30 shadow-[0_8px_32px_rgba(16,185,129,0.25)]',
            iconBgClass: 'bg-emerald-500/20',
            iconClass: 'text-emerald-400',
            textClass: 'text-emerald-100',
            labelClass: 'text-emerald-400/90',
            iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
            label: 'Excellent Match',
            description: 'Top tier priority. High alignment with ICP.',
            glowColor: 'rgba(16, 185, 129, 0.4)'
        };
    } else if (score >= 50) {
        return {
            // Advanced Glassmorphism for Below-Threshold Score (orange/amber warning)
            wrapperClass: 'bg-gradient-to-br from-orange-500/20 to-amber-500/10 backdrop-blur-2xl border border-orange-500/30 shadow-[0_8px_32px_rgba(249,115,22,0.25)]',
            iconBgClass: 'bg-orange-500/20',
            iconClass: 'text-orange-400',
            textClass: 'text-orange-100',
            labelClass: 'text-orange-400/90',
            iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
            label: 'Below ICP Threshold',
            description: 'Below 85% ICP threshold. Review before engaging.',
            glowColor: 'rgba(249, 115, 22, 0.4)'
        };
    } else {
        return {
            // Advanced Glassmorphism for Low Score
            wrapperClass: 'bg-gradient-to-br from-red-500/20 to-red-600/10 backdrop-blur-2xl border border-red-500/30 shadow-[0_8px_32px_rgba(239,68,68,0.25)]',
            iconBgClass: 'bg-red-500/20',
            iconClass: 'text-red-400',
            textClass: 'text-red-100',
            labelClass: 'text-red-400/90',
            iconPath: 'M6 18L18 6M6 6l12 12',
            label: 'Low Match',
            description: 'Low alignment. Proceed with caution.',
            glowColor: 'rgba(239, 68, 68, 0.4)'
        };
    }
}

export function markdownToHTML(markdown: string | null | undefined) {
    if (!markdown) return '';
    
    let html = markdown
        .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold text-white mt-6 mb-3">$1</h3>')
        .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold text-white mt-8 mb-4">$1</h2>')
        .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-white mt-10 mb-5">$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
        .replace(/\n\n/g, '</p><p class="text-neutral-400 leading-relaxed mb-4">')
        .replace(/^\- (.*$)/gim, '<li class="ml-4 mb-2">$1</li>')
        .replace(/(<li.*<\/li>)/s, '<ul class="list-disc list-inside mb-4 text-neutral-400">$1</ul>');
    
    if (!html.startsWith('<h') && !html.startsWith('<p') && !html.startsWith('<ul')) {
        html = '<p class="text-neutral-400 leading-relaxed mb-4">' + html + '</p>';
    }
    
    return html;
}

export function parseLinkedInSummary(summary: string) {
    if (!summary) return { sections: [] };

    const sections: {title: string, content: string}[] = [];
    const lines = summary.split('\n');
    let currentSection: string | null = null;
    let currentContent: string[] = [];

    lines.forEach(line => {
        const trimmed = line.trim();
        // Check if it's a section header (ALL CAPS or starts with emoji/special char)
        if (trimmed && (trimmed === trimmed.toUpperCase() || /^[🔸▪️●■]/.test(trimmed))) {
            if (currentSection) {
                sections.push({
                    title: currentSection,
                    content: currentContent.join('\n').trim()
                });
            }
            currentSection = trimmed.replace(/^[🔸▪️●■]\s*/, '');
            currentContent = [];
        } else if (trimmed) {
            currentContent.push(trimmed);
        }
    });

    if (currentSection) {
        sections.push({
            title: currentSection,
            content: currentContent.join('\n').trim()
        });
    }

    return { sections };
}

/**
 * Extracts years of experience from LinkedIn Summary text
 * Searches for patterns like "Experience Level: X years" or "X+ years of experience"
 *
 * @param summary LinkedIn summary text
 * @returns Number of years as string, or null if not found
 */
export function extractExperienceYearsFromSummary(summary: string): string | null {
    if (!summary) return null;

    // Pattern 1: "Experience Level: X years" or "Experience Level: X+ years"
    const pattern1 = /Experience Level:\s*(\d+)\+?\s*years?/i;
    const match1 = summary.match(pattern1);
    if (match1) return match1[1];

    // Pattern 2: "Years of Experience: X" or "X years of experience"
    const pattern2 = /(\d+)\+?\s*years?\s*of\s*experience/i;
    const match2 = summary.match(pattern2);
    if (match2) return match2[1];

    // Pattern 3: "X+ years" at the start of a line (common in summaries)
    const pattern3 = /(?:^|\n)\s*(\d+)\+?\s*years?\s*(?:in|of|experience)/im;
    const match3 = summary.match(pattern3);
    if (match3) return match3[1];

    return null;
}

/**
 * Extracts follower count from LinkedIn Summary "Network Size" section
 * Searches for patterns like "2,044 followers" or "Network Size: X connections | Y followers"
 *
 * @param summary LinkedIn summary text
 * @returns Number of followers as string, or null if not found
 */
export function extractFollowersFromSummary(summary: string): string | null {
    if (!summary) return null;

    // Pattern 1: "X followers" with optional comma formatting
    const pattern1 = /([\d,]+)\s*followers/i;
    const match1 = summary.match(pattern1);
    if (match1) return match1[1].replace(/,/g, ''); // Remove commas

    // Pattern 2: "Network Size: X connections | Y followers"
    const pattern2 = /Network Size[:\s]*[\d,]+\s*connections?\s*\|\s*([\d,]+)\s*followers?/i;
    const match2 = summary.match(pattern2);
    if (match2) return match2[1].replace(/,/g, '');

    return null;
}

/**
 * Gets the best available profile image URL
 * STRICT MODE: ONLY uses Storage URLs (Firebase/Appwrite), never external URLs
 *
 * This ensures all images are loaded from our own storage, preventing:
 * - Expired external URLs
 * - Rate limiting from external services
 * - Authentication issues with LinkedIn/Twitter
 * - Missing images when external services go down
 *
 * @param imageUrl The original image URL (e.g., LinkedIn, Framer) - IGNORED unless it's a storage URL
 * @param storageUrl Storage URL from Firebase or Appwrite (REQUIRED)
 * @param authToken Optional Firebase ID token for authenticated proxy requests
 * @returns Storage URL if available, undefined otherwise
 */
// Simple memoization cache for getProxiedImageUrl — same inputs always produce same output
const imageUrlCache = new Map<string, string | undefined>();

export function getProxiedImageUrl(imageUrl: string | null | undefined, storageUrl?: string | null | undefined, authToken?: string | null): string | undefined {
    const cacheKey = `${imageUrl ?? ''}|${storageUrl ?? ''}`;
    const cached = imageUrlCache.get(cacheKey);
    if (cached !== undefined || imageUrlCache.has(cacheKey)) return cached;

    const result = _computeProxiedImageUrl(imageUrl, storageUrl);
    imageUrlCache.set(cacheKey, result);
    // Evict when cache grows too large
    if (imageUrlCache.size > 500) {
        const firstKey = imageUrlCache.keys().next().value;
        if (firstKey !== undefined) imageUrlCache.delete(firstKey);
    }
    return result;
}

function _computeProxiedImageUrl(imageUrl: string | null | undefined, storageUrl?: string | null | undefined): string | undefined {
    // Convert Appwrite Storage URL to authenticated view URL
    const toAppwriteViewUrl = (url: string): string => {
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            const bucketsIdx = pathParts.indexOf('buckets');
            const filesIdx = pathParts.indexOf('files');
            if (bucketsIdx !== -1 && filesIdx !== -1) {
                const bucketId = pathParts[bucketsIdx + 1];
                const fileId = pathParts[filesIdx + 1];
                if (bucketId && fileId) {
                    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID || '693c0579001e8b06c6d6';
                    return `${urlObj.origin}/v1/storage/buckets/${bucketId}/files/${fileId}/view?project=${projectId}`;
                }
            }
            if (!url.includes('project=')) {
                const sep = url.includes('?') ? '&' : '?';
                return `${url}${sep}project=${import.meta.env.VITE_APPWRITE_PROJECT_ID || '693c0579001e8b06c6d6'}`;
            }
            return url;
        } catch {
            return url;
        }
    };

    // PRIORITY 1: Storage URL
    if (storageUrl) {
        if (storageUrl.includes('storage.googleapis.com')) return storageUrl;
        if (storageUrl.includes('appwrite') && storageUrl.includes('/storage/buckets/')) return toAppwriteViewUrl(storageUrl);
    }

    // PRIORITY 2: imageUrl as storage fallback
    if (imageUrl) {
        if (imageUrl.includes('storage.googleapis.com')) return imageUrl;
        if (imageUrl.includes('appwrite') && imageUrl.includes('/storage/buckets/')) return toAppwriteViewUrl(imageUrl);
    }

    // FALLBACK: external URL
    if (imageUrl && imageUrl.trim() !== '' && imageUrl !== 'undefined' && imageUrl !== 'null') return imageUrl;

    return undefined;
}

/**
 * Format revenue values to human-readable format (e.g., "952K/year", "1.5M/year")
 * Handles various input formats from N8N data
 *
 * @param revenue - Revenue value (string with currency symbols, numbers, ranges)
 * @returns Formatted revenue string or "UNKNOWN"
 *
 * @example
 * formatRevenue("$952,000") // "952K/year"
 * formatRevenue("1500000") // "1.5M/year"
 * formatRevenue("€1B+") // "1B+/year"
 * formatRevenue("$1M-$5M") // "1M-5M/year"
 */
export function formatRevenue(revenue: string | null | undefined): string {
    if (!revenue || revenue === 'Unknown' || revenue === 'Not available') return 'UNKNOWN';

    // Remove currency symbols and spaces
    const cleaned = revenue.replace(/[$€£¥₹,\s]/g, '');

    // Handle range format (e.g., "1M-5M", "$1M-$5M")
    if (cleaned.includes('-')) {
        const parts = cleaned.split('-');
        return `${parts[0]}-${parts[1]}/year`;
    }

    // Handle already formatted values (e.g., "1B+", "500K")
    if (/[KMB]\+?$/i.test(cleaned)) {
        return `${cleaned}/year`;
    }

    // Parse numeric value
    const numMatch = cleaned.match(/[\d.]+/);
    if (!numMatch) return revenue; // Return original if no number found

    const num = parseFloat(numMatch[0]);

    // Format to K, M, B
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(1).replace('.0', '')}B/year`;
    } else if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(1).replace('.0', '')}M/year`;
    } else if (num >= 1_000) {
        return `${(num / 1_000).toFixed(0)}K/year`;
    } else {
        return `${num}/year`;
    }
}

/**
 * Categorize revenue into predefined ranges for filtering
 * Handles various formats from N8N data:
 * - Raw numbers: 2465185 (number or string)
 * - Simple: "$50,000", "$5M", "€1B+"
 * - Spelled out: "EUR 1-2 billion", "1 million"
 * - Ranges: "$25M-$50M ARR", "$5B-$10B"
 * - Complex: "€1B+ (historically; €1.49B in 2009, +8% revenue growth H1 2025)"
 *
 * @param revenue - Revenue value (number, string with currency symbols, numbers, ranges)
 * @returns Revenue range category: '1-25K' | '25K-50K' | '50K-100K' | '100K-500K' | '500K-1M' | '1M-2M' | '2M-5M' | '5M-10M' | '10M-25M' | '25M-50M' | '50M-100M' | '100M-500M' | '500M-1B' | '1B+'
 *
 * @example
 * getRevenueRange(2465185) // "2M-5M"
 * getRevenueRange("2465185") // "2M-5M"
 * getRevenueRange("$15,000") // "1-25K"
 * getRevenueRange("$35,000") // "25K-50K"
 * getRevenueRange("$75,000") // "50K-100K"
 * getRevenueRange("$500,000") // "500K-1M"
 */
export function getRevenueRange(revenue: string | number | null | undefined): string | null {
    if (!revenue || revenue === 'Unknown' || revenue === 'Not available') return null;

    // Handle raw numbers directly
    if (typeof revenue === 'number') {
        return categorizeRevenueByAmount(revenue);
    }

    // Check if it's a pure number string (e.g., "2465185")
    const trimmed = revenue.trim();
    const pureNumber = parseFloat(trimmed);
    if (!isNaN(pureNumber) && /^\d+(\.\d+)?$/.test(trimmed)) {
        return categorizeRevenueByAmount(pureNumber);
    }

    // Normalize: lowercase, remove currency symbols and extra chars
    const normalized = revenue.toLowerCase().replace(/[$€£¥₹,\s+]/g, '');
    let numValue = 0;

    // Check for spelled-out numbers (billion, million, thousand)
    const billionMatch = revenue.match(/([\d.]+)\s*(billion|bil|bn)/i);
    const millionMatch = revenue.match(/([\d.]+)\s*(million|mil|mn)/i);
    const thousandMatch = revenue.match(/([\d.]+)\s*(thousand|k)\b/i);

    if (billionMatch) {
        numValue = parseFloat(billionMatch[1]) * 1_000_000_000;
    } else if (millionMatch) {
        numValue = parseFloat(millionMatch[1]) * 1_000_000;
    } else if (thousandMatch) {
        numValue = parseFloat(thousandMatch[1]) * 1_000;
    } else {
        // Try to parse with K/M/B suffixes (e.g., "1B", "50M", "100K")
        // Also handle ranges like "$25M-$50M" by taking the first value
        const match = normalized.match(/([\d.]+)([kmb])?/i);
        if (!match) return null;

        const num = parseFloat(match[1]);
        const suffix = match[2]?.toUpperCase();

        if (suffix === 'K') {
            numValue = num * 1_000;
        } else if (suffix === 'M') {
            numValue = num * 1_000_000;
        } else if (suffix === 'B') {
            numValue = num * 1_000_000_000;
        } else {
            // No suffix - check if it's already a large number (e.g., "1000000")
            numValue = num;
        }
    }

    // If we still have 0 or a very small number, return null
    if (numValue === 0) return null;

    return categorizeRevenueByAmount(numValue);
}

/**
 * Helper function to categorize a numeric revenue value into ranges
 * @param amount - Revenue amount as a number
 * @returns Revenue range category
 */
function categorizeRevenueByAmount(amount: number): string | null {
    if (amount < 25_000) return '1-25K';
    if (amount < 50_000) return '25K-50K';
    if (amount < 100_000) return '50K-100K';
    if (amount < 500_000) return '100K-500K';
    if (amount < 1_000_000) return '500K-1M';
    if (amount < 2_000_000) return '1M-2M';
    if (amount < 5_000_000) return '2M-5M';
    if (amount < 10_000_000) return '5M-10M';
    if (amount < 25_000_000) return '10M-25M';
    if (amount < 50_000_000) return '25M-50M';
    if (amount < 100_000_000) return '50M-100M';
    if (amount < 500_000_000) return '100M-500M';
    if (amount < 1_000_000_000) return '500M-1B';
    return '1B+';
}

/**
 * Construct Appwrite Storage URL from bucket and file IDs
 * Use this function to load images from Appwrite Storage buckets
 *
 * @param bucketId - Appwrite bucket ID (from .env.local)
 * @param fileId - File ID in the bucket (from bucket file list)
 * @returns Full Appwrite Storage URL with project ID
 *
 * @example
 * // Brainiacs logo from logos bucket
 * const logoUrl = getAppwriteStorageUrl('6940e3f8003c17bb7fc9', '6940e6360012a5ff6f2a');
 */
export function getAppwriteStorageUrl(bucketId: string, fileId: string): string {
    const APPWRITE_ENDPOINT = import.meta.env.VITE_APPWRITE_ENDPOINT || 'https://appwrite-prod-u12020.vm.elestio.app/v1';
    const APPWRITE_PROJECT_ID = import.meta.env.VITE_APPWRITE_PROJECT_ID || '693c0579001e8b06c6d6';
    return `${APPWRITE_ENDPOINT}/storage/buckets/${bucketId}/files/${fileId}/view?project=${APPWRITE_PROJECT_ID}`;
}

/**
 * Construct Appwrite Storage download URL from bucket and file IDs.
 * Use this for direct file downloads from authenticated browser sessions.
 */
export function getAppwriteStorageDownloadUrl(bucketId: string, fileId: string): string {
    const APPWRITE_ENDPOINT = import.meta.env.VITE_APPWRITE_ENDPOINT || 'https://appwrite-prod-u12020.vm.elestio.app/v1';
    const APPWRITE_PROJECT_ID = import.meta.env.VITE_APPWRITE_PROJECT_ID || '693c0579001e8b06c6d6';
    return `${APPWRITE_ENDPOINT}/storage/buckets/${bucketId}/files/${fileId}/download?project=${APPWRITE_PROJECT_ID}`;
}

/**
 * Construct Appwrite Storage view URL for in-browser PDF preview.
 */
export function getAppwriteStorageViewUrl(bucketId: string, fileId: string): string {
    const APPWRITE_ENDPOINT = import.meta.env.VITE_APPWRITE_ENDPOINT || 'https://appwrite-prod-u12020.vm.elestio.app/v1';
    const APPWRITE_PROJECT_ID = import.meta.env.VITE_APPWRITE_PROJECT_ID || '693c0579001e8b06c6d6';
    return `${APPWRITE_ENDPOINT}/storage/buckets/${bucketId}/files/${fileId}/view?project=${APPWRITE_PROJECT_ID}`;
}

/**
 * Format industry value - handles arrays and JSON strings
 * N8N sometimes returns industry as an array like ["Food and Beverage Services"]
 * This function safely extracts and formats the industry value
 *
 * @param industry - Industry value (string, array, or JSON string)
 * @returns Formatted industry string or fallback value
 *
 * @example
 * formatIndustry(["Food and Beverage"]) // "Food and Beverage"
 * formatIndustry('["Tech", "Software"]') // "Tech, Software"
 * formatIndustry("Technology") // "Technology"
 */
export function formatIndustry(industry: any): string {
    if (!industry) return '';

    // Handle array directly
    if (Array.isArray(industry)) {
        return industry.join(', ');
    }

    // Handle string (might be JSON)
    if (typeof industry === 'string') {
        // Try to parse as JSON array
        try {
            const parsed = JSON.parse(industry);
            if (Array.isArray(parsed)) {
                return parsed.join(', ');
            }
        } catch {
            // Not JSON, return as-is
        }
        return industry;
    }

    return String(industry);
}

/**
 * Format lead industry with CNAE preference
 * Format: "XX.XX CNAE TEXT" (code first, then text)
 * Handles multiple values by taking the first one
 */
export function formatLeadIndustry(lead: { cnae_code?: string | null, cnae_text?: string | null, companyIndustry?: string | any, industry?: string | any }): string {
    // FIX: CNAE text already includes the code, no need to prepend it again
    // Example: cnae_text = "49.41 Freight transport 52.29 Other activities"
    // We need to extract ONLY the first code + description pair
    if (lead.cnae_code && lead.cnae_text) {
        const cnaeText = String(lead.cnae_text).trim();

        // Try comma-separated first
        if (cnaeText.includes(',')) {
            return cnaeText.split(',')[0].trim();
        }

        // Extract first CNAE entry (pattern: "XX.XX Description")
        // Match: digits.digits followed by text until next digit pattern or end
        const match = cnaeText.match(/^(\d+\.\d+\s+[^\d]+?)(?=\d+\.\d+|$)/);
        if (match) {
            return match[1].trim();
        }

        // Fallback: return everything before second CNAE code pattern
        const parts = cnaeText.split(/\s+(?=\d+\.\d+)/);
        return parts[0].trim();
    }

    // Fallback to standard industry formatting
    return formatIndustry(lead.companyIndustry || lead.industry);
}

/**
 * Format lead industry with CNAE preference - returns ALL codes as an array
 * Format: "XX.XX CNAE TEXT" (code first, then text)
 * Returns array for displaying multiple values on separate lines
 */
export function formatLeadIndustryArray(lead: { cnae_code?: string | null, cnae_text?: string | null, companyIndustry?: string | any, industry?: string | any }): string[] {
    if (lead.cnae_code && lead.cnae_text) {
        const codes = String(lead.cnae_code).split(',').map(c => c.trim()).filter(Boolean);
        const texts = String(lead.cnae_text).split(',').map(t => t.trim()).filter(Boolean);

        // Map each code with its corresponding text
        return codes.map((code, index) => {
            const text = texts[index] || '';

            // Check if text already contains the code (e.g., "7112 - Engineering")
            if (text && text.includes(code)) {
                const cleanText = text.replace(new RegExp(`^${code}\\s*[-–—:]?\\s*`), '').trim();
                return cleanText ? `${code} ${cleanText}` : code;
            }
            return text ? `${code} ${text}` : code;
        });
    }

    // Fallback to standard industry formatting (single value as array)
    const industry = formatIndustry(lead.companyIndustry || lead.industry);
    return industry ? [industry] : [];
}

/**
 * Get the effective user email considering impersonation state
 * When a SUPERUSER is impersonating another user, returns the impersonated email
 * Otherwise returns the current user's email
 *
 * @param targetEmail - The impersonation target email (from useAuth)
 * @param currentUserEmail - The current logged-in user's email
 * @returns The effective email to use for N8N actions
 *
 * @example
 * // When impersonating
 * getEffectiveUserEmail('user@example.com', 'admin@brainiacsbots.com')
 * // Returns: 'user@example.com'
 *
 * // When not impersonating
 * getEffectiveUserEmail(null, 'user@example.com')
 * // Returns: 'user@example.com'
 */
export function getEffectiveUserEmail(targetEmail: string | null, currentUserEmail: string | null): string | null {
    return targetEmail || currentUserEmail;
}

/**
 * Get the effective user ID considering impersonation state
 * When a SUPERUSER is impersonating another user, returns the impersonated user's client_id
 * Otherwise returns the current user's uid
 *
 * @param targetEmail - The impersonation target email (from useAuth)
 * @param currentUserUid - The current logged-in user's uid
 * @param clientProfile - The client profile object (contains client_id)
 * @returns The effective user ID to use for N8N actions
 *
 * @example
 * // When impersonating and we have the impersonated user's profile
 * getEffectiveUserId('user@example.com', 'admin-uid', { client_id: 'user-client-id', ... })
 * // Returns: 'user-client-id'
 *
 * // When not impersonating
 * getEffectiveUserId(null, 'user-uid', { client_id: 'user-client-id', ... })
 * // Returns: 'user-uid'
 */
export function getEffectiveUserId(
    targetEmail: string | null,
    currentUserUid: string | null,
    clientProfile: { client_id: string } | null
): string {
    // If impersonating (targetEmail is set) and we have the impersonated user's profile
    // Return the impersonated user's client_id
    if (targetEmail && clientProfile?.client_id) {
        return clientProfile.client_id;
    }
    // Otherwise return the current user's uid
    return currentUserUid || '';
}
